<template>
  <!-- Terms and Conditions Modal Component -->    
  <TermsAndConditionsModal v-model="showTermsAndConditionsModal" :show-privacy-section="showPrivacySection" :show-terms-section="!showPrivacySection" @accepted="acceptTerms = true" />
  
  <div v-if="showBanner" class="cookies-country-banner">
    <div class="banner-content">
      <h4>{{ translate('cookies.welcome', 'Welcome to PawPulse!') }}</h4>
      <p>{{ translate('cookies.select_country', 'Please select your country:') }}</p>
      
      <div class="country-selection">
        <!-- Colombia (WhatsApp) -->
        <button 
          :class="['country-button', selectedCountry === 'co' ? 'active' : '']" 
          @click="selectCountry('co')">
          <span class="flag">🇨🇴</span>
          <span>{{ translate('cookies.colombia', 'Colombia') }}</span>
        </button>
        
        <!-- United States (SMS) -->
        <button 
          :class="['country-button', selectedCountry === 'us' ? 'active' : '']" 
          @click="selectCountry('us')">
          <span class="flag">🇺🇸</span>
          <span>{{ translate('cookies.united_states', 'United States') }}</span>
        </button>
      </div>
      
      <div class="cookies-text">
        <p>{{ translate('cookies.usage_info', 'This site uses cookies to enhance your experience.') }}</p> <br>
        <p>{{ translate('cookies.terms_and_conditions_banner', 'By continuing to use this site, you agree to our') }}
        <a href="#" @click.prevent="openTermsModal()">{{ translate('auth.terms_and_conditions', 'Terms and conditions') }}</a> 
        {{ translate('common.and', 'and') }} 
        <a href="#" @click.prevent="openPrivacySectionModal()">{{ translate('auth.privacy_policy_two', 'Privacy Policy') }}</a></p>
      </div>

      
      <div class="action-buttons">
        <button class="btn btn-primary accept-button" @click="acceptCookies">
          {{ translate('cookies.accept', 'Accept Cookies') }}
        </button>
        <button class="btn btn-secondary reject-button" @click="rejectCookies">
          {{ translate('cookies.reject', 'Reject Cookies') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import TermsAndConditionsModal from './TermsAndConditionsModal.vue'

export default {
  name: 'CookiesCountryBanner',
  components: {
    TermsAndConditionsModal
  },
  setup() {
    const store = useStore()
    const showBanner = ref(false)
    const selectedCountry = ref('')
    const showTermsAndConditionsModal = ref(false)
    const showPrivacySection = ref(false)
    const acceptTerms = ref(false)

    // Check if banner should be shown on mount
    onMounted(() => {
      const hasSeenBanner = localStorage.getItem('hasSeenBanner') === 'true'
      const savedCountry = localStorage.getItem('selectedCountry')
      
      // set selected country if exists
      if (savedCountry) {
        selectedCountry.value = savedCountry
        applyCountrySelection(savedCountry)
      }
      
      // show banner if first time
      if (!hasSeenBanner) {
        showBanner.value = true
      }
    })
    
    // Select country
    const selectCountry = (country) => {
      selectedCountry.value = country
      // set selected country in localStorage
      localStorage.setItem('selectedCountry', country)
      // Apply country selection
      applyCountrySelection(country)
    }
    
    // Apply country selection to application state
    const applyCountrySelection = (country) => {
      // set country if not exists
      if (!country) {
        country = localStorage.getItem('selectedCountry') || 'us'
      }
      
      // Update reactive reference
      selectedCountry.value = country
      
      // save selected country in localStorage
      localStorage.setItem('selectedCountry', country)
      
      // Map country code to language code for the store
      const languageMap = {
        'co': 'es', // Colombia -> Spanish (WhatsApp)
        'us': 'en'  // United States -> English (SMS)
      }
      
      const language = languageMap[country] || 'en'
      
      // Store the selection in localStorage
      localStorage.setItem('selectedCountry', country)
      
      // Update application language (which also affects messaging channel)
      store.dispatch('fetchTranslations', language)
      
      // Dispatch custom event to notify language change
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'selectedCountry',
        newValue: country
      }))
      
      // Force reload of Tawk.to widget
      if (window.Tawk_API) {
        window.Tawk_API.endChat()
        window.Tawk_API = null
        
        // Remove existing script
        const existingScript = document.querySelector('script[src*="tawk.to"]')
        if (existingScript) {
          existingScript.remove()
        }
        
        // Initialize widget with new configuration
        const { initializeTawkWidget } = require('@/utils/tawk')
        initializeTawkWidget()
      }
    }
    
    // Accept cookies and save preferences
    const acceptCookies = () => {
      // set selected country if not exists
      if (!selectedCountry.value) {
        const savedCountry = localStorage.getItem('selectedCountry') || 'us'
        selectedCountry.value = savedCountry
      }
      
      // save selected country in localStorage
      localStorage.setItem('selectedCountry', selectedCountry.value)
      
      // Apply country selection
      applyCountrySelection(selectedCountry.value)
      
      // Save preferences
      localStorage.setItem('acceptsCookies', 'true')
      localStorage.setItem('hasSeenBanner', 'true')
      
      // Hide banner
      showBanner.value = false
    }
    
    // Reject cookies but still save country preference
    const rejectCookies = () => {
      // set selected country if not exists
      if (!selectedCountry.value) {
        const savedCountry = localStorage.getItem('selectedCountry') || 'us'
        selectedCountry.value = savedCountry
      }
      
      // save selected country in localStorage
      localStorage.setItem('selectedCountry', selectedCountry.value)
      
      // Apply country selection
      applyCountrySelection(selectedCountry.value)
      
      // Save preferences
      localStorage.setItem('acceptsCookies', 'false')
      localStorage.setItem('hasSeenBanner', 'true')
      
      // Hide banner
      showBanner.value = false
    }
    
    // Translation helper function
    const translate = (key, fallback) => {
      const translations = store.state.translations
      const parts = key.split('.')
      
      let value = translations
      for (const part of parts) {
        if (value && value[part]) {
          value = value[part]
        } else {
          return fallback
        }
      }
      
      return typeof value === 'string' ? value : fallback
    }
  
    // Modal handlers
    const acceptAndCloseTerms = () => {
      acceptTerms.value = true;
      showTermsAndConditionsModal.value = false;
      showPrivacySection.value = false;
    }
    
    // Open terms modal
    const openTermsModal = () => {
      showTermsAndConditionsModal.value = true;
      showPrivacySection.value = false;
    }
    
    // Open privacy modal
    const openPrivacySectionModal = () => {
      showTermsAndConditionsModal.value = true;
      showPrivacySection.value = true;
    }
    
    return {
      showBanner,
      selectedCountry,
      selectCountry,
      acceptCookies,
      rejectCookies,
      translate,
      showTermsAndConditionsModal,
      showPrivacySection,
      acceptAndCloseTerms,
      openTermsModal,
      openPrivacySectionModal
    }
  }
}
</script>

<style scoped>
.cookies-country-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  z-index: 1000;
  border-top: 1px solid #ddd;
}

.banner-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.banner-content h4 {
  margin-bottom: 15px;
  color: var(--primary-blue);
}

.country-selection {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
}

.country-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.country-button.active {
  border-color: var(--primary-orange);
  background-color: #fff8f0;
}

.country-button:hover {
  border-color: var(--primary-orange);
}

.flag {
  font-size: 32px;
  margin-bottom: 8px;
}

.cookies-text {
  margin: 15px 0;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px; 
}

 p {
  font-size: 16px;
}
@media (max-width: 576px) {
  .country-selection {
    flex-direction: column;
    align-items: center;
  }
  
  .country-button {
    width: 80%;
  }
}
</style>
